<template>
  <div class="page-card">
    <div class="page-head">
      <h3>Policy Documents</h3>
      <el-button type="primary" size="small" @click="openCreate">New Document</el-button>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Keyword">
        <el-input v-model="query.keyword" clearable placeholder="Title / summary / content" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="Type">
        <el-select v-model="query.documentType" clearable placeholder="All">
          <el-option v-for="item in typeOptions" :key="item" :label="item" :value="item" />
        </el-select>
      </el-form-item>
      <el-form-item label="Status">
        <el-select v-model="query.status" clearable placeholder="All">
          <el-option label="published" value="published" />
          <el-option label="draft" value="draft" />
          <el-option label="archived" value="archived" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="title" label="Title" min-width="180" show-overflow-tooltip />
      <el-table-column prop="documentType" label="Type" width="140" />
      <el-table-column prop="version" label="Version" width="90" />
      <el-table-column prop="status" label="Status" width="110">
        <template slot-scope="scope">
          <el-tag :type="statusType(scope.row.status)" size="small">{{ scope.row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="summary" label="Summary" min-width="260" show-overflow-tooltip />
      <el-table-column prop="sortOrder" label="Sort" width="80" />
      <el-table-column prop="effectiveDate" label="Effective At" min-width="160" />
      <el-table-column label="Actions" width="150" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">Edit</el-button>
          <el-button type="text" size="small" class="danger" @click="remove(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="760px">
      <el-form :model="form" label-width="120px" size="small">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="Title">
              <el-input v-model="form.title" placeholder="Privacy Policy" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Type">
              <el-select v-model="form.documentType" filterable allow-create default-first-option style="width:100%">
                <el-option v-for="item in typeOptions" :key="item" :label="item" :value="item" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Version">
              <el-input v-model="form.version" placeholder="1.0" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Status">
              <el-select v-model="form.status" style="width:100%">
                <el-option label="published" value="published" />
                <el-option label="draft" value="draft" />
                <el-option label="archived" value="archived" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Sort">
              <el-input-number v-model="form.sortOrder" :min="0" style="width:100%" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="Effective At">
          <el-date-picker
            v-model="form.effectiveDate"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="Summary">
          <el-input v-model="form.summary" type="textarea" :rows="3" placeholder="Short description shown in the app list." />
        </el-form-item>
        <el-form-item label="Content">
          <el-input v-model="form.content" type="textarea" :rows="12" placeholder="Full policy content shown in the app detail page." />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible=false">Cancel</el-button>
        <el-button type="primary" @click="submit">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { policyApi } from '@/api/system'

const emptyQuery = () => ({ pageNum: 1, pageSize: 10, keyword: '', documentType: '', status: '' })

const emptyForm = () => ({
  id: null,
  title: '',
  documentType: 'Privacy Policy',
  summary: '',
  content: '',
  version: '1.0',
  status: 'published',
  sortOrder: 0,
  effectiveDate: ''
})

export default {
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: emptyQuery(),
      dialogVisible: false,
      dialogTitle: 'New Policy Document',
      form: emptyForm(),
      typeOptions: ['Privacy Policy', 'Terms', 'Data', 'Consent', 'Safety']
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const res = await policyApi.list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = emptyQuery()
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openCreate() {
      this.dialogTitle = 'New Policy Document'
      this.form = emptyForm()
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = 'Edit Policy Document'
      this.form = { ...emptyForm(), ...row }
      this.dialogVisible = true
    },
    async submit() {
      if (!this.form.title) {
        this.$message.warning('Please enter a title')
        return
      }
      if (!this.form.content) {
        this.$message.warning('Please enter document content')
        return
      }
      if (this.form.id) await policyApi.update(this.form)
      else await policyApi.create(this.form)
      this.$message.success('Saved successfully')
      this.dialogVisible = false
      this.loadData()
    },
    async remove(row) {
      await this.$confirm(`Delete policy document "${row.title}"?`, 'Confirm', { type: 'warning' })
      await policyApi.remove(row.id)
      this.$message.success('Deleted successfully')
      this.loadData()
    },
    statusType(status) {
      if (status === 'published') return 'success'
      if (status === 'draft') return 'warning'
      return 'info'
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
</style>
